import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { Button } from '../../../common-components/Button/Button';
import { Field, FieldType } from '../../../common-components/Field/Field';
import { If } from '../../../common-components/If/If';
import ServicesContext from '../../../contexts/ServicesContext';
import { AssemblyType } from '../../../enums/AssemblyType';
import { strings } from '../../../resources/strings';
import { CreateLocationInput } from '../../../services/Locations/CreateLocation/CreateLocationInput';
import { assemblyTypeOptions, toApiDateTime, yesNoOptions } from '../locationUi';
import { ContainerBody, ErrorList, RequiredHint, Row } from './styles';

interface IModalNewLocationProps {
  close: () => void;
  finish: () => void;
}

const toBoolean = (value: string) => value === 'true' ? true : value === 'false' ? false : undefined;

const ModalNewLocation: React.FC<IModalNewLocationProps> = ({
  close,
  finish
}) => {

  const { createLocationService, toastService, alertService } = React.useContext(ServicesContext);

  const [shopifyId, setShopifyId] = React.useState<string>('');
  const [name, setName] = React.useState<string>('');
  const [isOmnichannel, setIsOmnichannel] = React.useState<string>('');
  const [manageOrderInSislog, setManageOrderInSislog] = React.useState<string>('');
  const [manageOrderInSislogSinceDate, setManageOrderInSislogSinceDate] = React.useState<Date | null>(null);
  const [communicateClientDeadline, setCommunicateClientDeadline] = React.useState<string>('');
  const [communicateClientDeadlineSinceDate, setCommunicateClientDeadlineSinceDate] = React.useState<Date | null>(null);
  const [assemblyType, setAssemblyType] = React.useState<string>(`${AssemblyType.All}`);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState<boolean>(false);

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Nova loja</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        <ContainerBody>
          <RequiredHint>Todos os campos são obrigatórios. As demais informações podem ser preenchidas depois.</RequiredHint>
          <Row>
            <Field
              type={FieldType.Number}
              label={'ID Shopify'}
              value={shopifyId}
              change={(value) => setShopifyId(value)}
              autoFocus={true}
              marginTop={'0px'}
              numberMin={1}
            />
          </Row>
          <Row>
            <Field
              type={FieldType.Text}
              label={'Nome'}
              value={name}
              change={(value) => setName(value)}
              marginTop={'0px'}
              maxLength={250}
            />
          </Row>
          <Row>
            <Field
              type={FieldType.Select}
              label={'Omnichannel'}
              selectProps={{ options: yesNoOptions, displayField: 'text', keyField: 'key' }}
              value={isOmnichannel}
              change={(value) => setIsOmnichannel(value)}
              marginTop={'0px'}
            />
          </Row>
          <Row>
            <Field
              type={FieldType.Select}
              label={'Usa Sislog para gestão de pedidos'}
              selectProps={{ options: yesNoOptions, displayField: 'text', keyField: 'key' }}
              value={manageOrderInSislog}
              change={(value) => setManageOrderInSislog(value)}
              marginTop={'0px'}
            />
            <If condition={manageOrderInSislog === 'true'}>
              <Field
                type={FieldType.DateTimePicker}
                label={'Desde'}
                value={manageOrderInSislogSinceDate}
                change={(value) => setManageOrderInSislogSinceDate(value)}
                marginTop={'0px'}
              />
            </If>
          </Row>
          <Row>
            <Field
              type={FieldType.Select}
              label={'Comunica prazo ao cliente'}
              selectProps={{ options: yesNoOptions, displayField: 'text', keyField: 'key' }}
              value={communicateClientDeadline}
              change={(value) => setCommunicateClientDeadline(value)}
              marginTop={'0px'}
            />
            <If condition={communicateClientDeadline === 'true'}>
              <Field
                type={FieldType.DateTimePicker}
                label={'Para pedidos a partir de'}
                value={communicateClientDeadlineSinceDate}
                change={(value) => setCommunicateClientDeadlineSinceDate(value)}
                marginTop={'0px'}
              />
            </If>
          </Row>
          <Row>
            <Field
              type={FieldType.Select}
              label={'Tipo de montagem'}
              selectProps={{ options: assemblyTypeOptions, displayField: 'text', keyField: 'key' }}
              value={assemblyType}
              change={(value) => setAssemblyType(value)}
              marginTop={'0px'}
            />
          </Row>
          <If condition={errors.length > 0}>
            <ErrorList>
              {errors.map(error => <li key={error}>{error}</li>)}
            </ErrorList>
          </If>
        </ContainerBody>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button text={strings.Cancel} click={() => close()} marginRight={'10px'} disabled={saving} />
          <Button text={strings.Save} click={() => save()} marginLeft={'10px'} disabled={saving} />
        </div>
      </Modal.Footer>
    </Modal>
  );

  function validate(): string[] {
    const list: string[] = [];
    if (!(+shopifyId > 0)) list.push('Informe o ID Shopify.');
    if (!name.trim()) list.push('Informe o nome.');
    if (toBoolean(isOmnichannel) === undefined) list.push('Informe se a loja é omnichannel.');
    if (toBoolean(manageOrderInSislog) === undefined) list.push('Informe se a loja usa o Sislog.');
    else if (manageOrderInSislog === 'true' && !manageOrderInSislogSinceDate) list.push('Informe desde quando a loja usa o Sislog.');
    if (toBoolean(communicateClientDeadline) === undefined) list.push('Informe se a loja comunica prazo ao cliente.');
    else if (communicateClientDeadline === 'true' && !communicateClientDeadlineSinceDate) list.push('Informe desde quando a loja comunica prazo ao cliente.');
    return list;
  }

  function save() {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;

    const input = new CreateLocationInput();
    input.shopifyId = +shopifyId;
    input.name = name.trim();
    input.isOmnichannel = toBoolean(isOmnichannel)!;
    input.manageOrderInSislog = toBoolean(manageOrderInSislog)!;
    input.manageOrderInSislogSinceDate = input.manageOrderInSislog ? toApiDateTime(manageOrderInSislogSinceDate) : null;
    input.communicateClientDeadline = toBoolean(communicateClientDeadline)!;
    input.communicateClientDeadlineForOrderAfterDate = input.communicateClientDeadline ? toApiDateTime(communicateClientDeadlineSinceDate) : null;
    input.assemblyType = +assemblyType as AssemblyType;

    setSaving(true);
    createLocationService.handle(input)
      .then(result => {
        if (result?.value?.result) {
          toastService.success('Loja cadastrada.');
          finish();
          return;
        }
        setErrors(result?.value?.errors?.length ? result.value.errors : ['Não foi possível salvar a loja.']);
      })
      .catch(error => {
        if (error?.response) {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        } else {
          alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
        }
      })
      .finally(() => setSaving(false));
  }
}

export { ModalNewLocation };
